import { Anchor, Box, Button, Text } from '@mantine/core';
import { OTP_DIGITS_NUMBER } from 'consts';
import { usePostOtpLogin, usePostOtpQuery } from 'features/auth/api/otp';
import { useGetAuthOtp } from 'features/document-invitation/api/otp';
import { getZodSchemaErrorMessages } from 'helpers';
import { useServerErrors, useShowNotification } from 'hooks';
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TextInput } from 'shared/ui/Inputs';
import type { ValidationErrors } from 'types';
import { z } from 'zod';

import { AuthContainerLoginHeader } from '../../AuthContainerLoginHeader';
import { LoginOptionsDivider } from '../LoginOptionsDivider';
import { SsoLoginMethods } from '../SsoLoginMethods';
import { LoginOTPCountdown } from './LoginOTPCountdown';
import { LoginOTPResendCode } from './LoginOTPResendCode';

type Props = {
  hasLoginMethodPassword: boolean;
  switchLoginMethod: () => void;
  onSuccess: (accessToken: string) => void;
  isOnDocumentInvitationRoute: boolean;
  identifier?: string;
  backButtonAction?: () => void;
};

export const LoginOTP = ({
  hasLoginMethodPassword,
  switchLoginMethod,
  onSuccess,
  isOnDocumentInvitationRoute,
  identifier = '',
  backButtonAction,
}: Props) => {
  const { t } = useTranslation(['common', 'user']);
  const [tryAutoSend, setTryAutoSend] = useState(false);
  const { invitationId } = useParams();

  const schema = z.object({
    code: z
      .string()
      .min(1, { message: t('formErrors.required', { ns: 'common' }) })
      .max(OTP_DIGITS_NUMBER, {
        message: t('formErrors.exactNumberOfSigns', {
          ns: 'common',
          number: OTP_DIGITS_NUMBER,
        }),
      }),
  });

  const { getGeneralServerError, getServerErrorStatus } = useServerErrors();
  const showNotification = useShowNotification();

  const codeInputRef = useRef<HTMLInputElement>(null);

  const [code, setCode] = useState('');
  const [errorStatus, setErrorStatus] = useState<ValidationErrors | null>(null);
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const { mutateAsync: otpLogin, isLoading: isOtpLoginLoading } =
    usePostOtpLogin();

  useEffect(() => {
    if (code?.length === 6 && !errorStatus?.code && !tryAutoSend) {
      setTryAutoSend(true);
      checkCode();
    }
  }, [code, errorStatus]);

  const handleServerErrors = (error: unknown) => {
    const validationServerErrors = getServerErrorStatus(error);

    if (validationServerErrors) {
      setErrorStatus(validationServerErrors);
    } else {
      setServerErrorMessage(getGeneralServerError(error));
    }

    codeInputRef.current?.focus();
  };

  const isPhoneIdentifier = !identifier.includes('@');

  const {
    isFetching: isOtpCodeFetching,
    refetch: otpCodeRefetch,
    data: otpCodeData,
  } = usePostOtpQuery(
    {
      bodyParams: isPhoneIdentifier
        ? { phone: identifier }
        : { email: identifier },
    },
    {
      enabled: !isOnDocumentInvitationRoute,
      onError: handleServerErrors,
      onSuccess: () => {
        showNotification({
          message: t('codeHasBeenSent', { ns: 'user' }),
        });
      },
    },
  );
  const {
    isFetching: isInvitationCodeFetching,
    refetch: invitationCodeRefetch,
    data: invitationCode,
  } = useGetAuthOtp(
    {
      pathParams: {
        invitationId: invitationId as string,
      },
    },
    {
      enabled: isOnDocumentInvitationRoute,
    },
  );

  const isCodeFetching = isOnDocumentInvitationRoute
    ? isInvitationCodeFetching
    : isOtpCodeFetching;
  const refetch = isOnDocumentInvitationRoute
    ? invitationCodeRefetch
    : otpCodeRefetch;
  const data = isOnDocumentInvitationRoute ? invitationCode : otpCodeData;

  const {
    id: otpId = '',
    order = '_',
    validUntil = '',
    method = 'email',
  } = (data ?? {}) as {
    id?: string;
    order?: number | string;
    validUntil?: string;
    method?: 'email' | 'sms';
  };
  const isSmsMethod = method === 'sms';

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, OTP_DIGITS_NUMBER);
    setErrorStatus(status => ({
      ...status,
      code: '',
    }));
    setCode(value);
  };

  const handleResendCode = () => {
    setCode('');
    refetch();
  };

  const checkCode = async () => {
    const errors = getZodSchemaErrorMessages({
      schema,
      fields: { code },
    });

    if (errors) {
      setErrorStatus(status => ({
        ...status,
        code: errors?.code,
      }));
    } else {
      try {
        const { accessToken } = await otpLogin({
          otpId,
          code,
        });

        onSuccess(accessToken);
      } catch (error) {
        handleServerErrors(error);
      }
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await checkCode();
  };

  useEffect(() => {
    if (errorStatus?.code) {
      codeInputRef.current?.focus();
    }
  }, [errorStatus?.code]);

  return (
    <>
      <AuthContainerLoginHeader
        errorLabel={serverErrorMessage}
        backButtonAction={backButtonAction}
      />
      <form onSubmit={handleSubmit}>
        <TextInput
          id="identifier"
          label={
            identifier.includes('@')
              ? t('email', { ns: 'common' })
              : t('phoneNumber', { ns: 'common' })
          }
          disabled
          value={identifier}
        />
        <Box
          sx={{
            input: {
              letterSpacing: 10,
            },
          }}
          component={TextInput}
          id="code"
          label={
            <>
              <div>
                {t(isSmsMethod ? 'codeValidUntilSms' : 'codeValidUntil', {
                  ns: 'user',
                  codeNumber: order,
                })}{' '}
                <LoginOTPCountdown key={validUntil} validUntil={validUntil} />
              </div>
              <Text
                sx={{
                  whiteSpace: 'break-spaces',
                }}
                span
                size="xs"
              >
                {t(isSmsMethod ? 'otpCodeInsertSms' : 'otpCodeInsert', {
                  ns: 'user',
                  digits: OTP_DIGITS_NUMBER,
                })}
              </Text>
            </>
          }
          placeholder="000000"
          autoComplete="off"
          disabled={isOtpLoginLoading || isCodeFetching}
          value={code}
          error={errorStatus?.code}
          ref={codeInputRef}
          onChange={handleCodeChange}
        />
        <Button
          sx={theme => ({
            marginBottom: theme.other.spacing(2),
            fontWeight: 600,
          })}
          type="submit"
          fullWidth
          size="lg"
          loading={isOtpLoginLoading}
          disabled={isCodeFetching}
        >
          {t('signIn', { ns: 'user' })}
        </Button>
        {!isOtpLoginLoading && (
          <LoginOTPResendCode
            isLoading={isCodeFetching}
            resendCode={handleResendCode}
            isSmsMethod={isSmsMethod}
          />
        )}
      </form>
      {hasLoginMethodPassword && !isOtpLoginLoading && !isCodeFetching && (
        <Anchor
          component="button"
          sx={{
            width: '100%',
            textAlign: 'center',
          }}
          onClick={switchLoginMethod}
        >
          {t('wishPassword', { ns: 'user' })}
        </Anchor>
      )}
      <LoginOptionsDivider />
      <SsoLoginMethods />
    </>
  );
};
